import { computed, inject, Injectable, type Signal } from '@angular/core';
import { type AccountModuleKey } from '@app/reducers/account-module/account-module.model';
import { accountModuleStore } from '@app/reducers/account-module/account-module.state';
import { getAccountSubscription } from '@app/reducers/account/account-service.selectors';
import { getPermissionState } from '@app/reducers/auth/permission.helper';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { hasPermission, type PermissionOption } from '@sb/shared/permission/util';

import {
  countResolved,
  deriveStepViews,
  hasNothingLeftToDo,
  planLockedSteps,
  type PayrollStepAvailability,
} from './payroll-progress';
import { PayrollRunService } from './payroll-run.service';
import { usePayrollStepSources } from './payroll-sources';
import { payrollStepDefinitions, payrollStepsForModules } from './payroll-steps';
import { type PayrollStepId, type PayrollStepView } from './payroll.model';

/**
 * Owns the derived step state for the payroll run: every step's status and the resolved/total
 * counts for the progress bar. Every step the account's modules allow is present for it — one the
 * manager cannot act on reads 'blocked', one the account's plan does not include reads
 * 'plan-locked', and neither is left out. A step whose module is off is the one case that does
 * leave the run entirely.
 *
 * Both of those unavailability checks are made here, once, and every other answer about a run's
 * progress is taken from `views` — so the progress denominator, the report's warning list, the
 * Done button and the exit guard all follow from this one derivation.
 *
 * A plain signals service, not a signal store — this is pure derivation over `PayrollRunService`
 * and permission state, with no backend call, entity collection or request status of its own.
 *
 * Shared rather than component-local because the progress panel (side panel) and the payroll
 * page render side by side and must show identical progress; duplicating this ~30-line
 * derivation in both components would drift the moment a rule changes.
 */
@Injectable({ providedIn: 'root' })
export class PayrollProgressService {
  #run = inject(PayrollRunService);
  #store = inject<Store<AppState>>(Store);
  #translate = inject(TranslateService);

  #permissions = this.#store.selectSignal(getPermissionState);
  #subscription = this.#store.selectSignal(getAccountSubscription);

  // One selector signal per distinct module the definitions gate on, derived from them so a module
  // gated on in `payroll-steps.ts` cannot be silently absent here.
  #moduleEnabled = new Map<AccountModuleKey, Signal<boolean>>(
    [...new Set(payrollStepDefinitions.flatMap((step) => (step.module ? [step.module] : [])))].map((moduleKey) => [
      moduleKey,
      this.#store.selectSignal(accountModuleStore.selectModuleEnabled(moduleKey)),
    ]),
  );

  #enabledModules = computed<AccountModuleKey[]>(() =>
    [...this.#moduleEnabled].filter(([, isEnabled]) => isEnabled()).map(([moduleKey]) => moduleKey),
  );

  #definitions = computed(() => payrollStepsForModules(payrollStepDefinitions, this.#enabledModules()));

  // Every step now has a source: `lock` (open days), `balances` (plus/min), `absences`,
  // `timesheets` and `minijob`. With no source for a step, its `outstanding` count is unmeasured,
  // so `deriveStepViews` leaves it 'outstanding' — correct behaviour, not a bug: a step with no
  // measurable backlog can never resolve itself.
  #sources = usePayrollStepSources(this.#run.scope, this.#run.timesheetGroups, this.#run.timesheetsUnavailable);

  // Any-of across the run's own departments: these permissions are granted per department, so a
  // manager who may approve timesheets in one of the departments the run covers can act on that
  // step for that department, and the step is theirs to work rather than blocked outright.
  #hasStepPermission = (permission: PermissionOption): boolean =>
    hasPermission(
      { permissions: permission, userId: 'me', departments: this.#run.scope()?.departmentIds ?? 'any' },
      this.#permissions(),
    );

  views = computed<PayrollStepView[]>(() => {
    const definitions = this.#definitions();

    // Which steps are unavailable, and for which of the two reasons, decided here and handed over
    // as data — the same shape as the skipped set beside them, so `deriveStepViews` reads all
    // three the same way rather than calling back into permission or plan state per step.
    const availability: PayrollStepAvailability = {
      skipped: this.#run.skipped(),
      blocked: new Set(definitions.filter((step) => !this.#hasStepPermission(step.permission)).map((step) => step.id)),
      planLocked: planLockedSteps(definitions, this.#subscription()),
    };

    return deriveStepViews(definitions, this.#sources(), availability, (key, params) =>
      this.#translate.instant(key, params),
    );
  });

  /**
   * Outstanding items per step, for the steps that have a measurable backlog at all. A step is
   * absent rather than zero when its source is missing — the report step, which has no backlog,
   * and a source that has not loaded or covers departments the manager cannot see into. That is
   * the same distinction `deriveStepViews` relies on to keep an unloaded source from reading as a
   * cleared step, and tracking has to preserve it: a reported 0 for an unknown count is a false
   * all-clear in the data.
   */
  pendingCounts = computed<Partial<Record<PayrollStepId, number>>>(() => {
    const sources = this.#sources();
    const counts: Partial<Record<PayrollStepId, number>> = {};

    for (const view of this.views()) {
      const source = sources[view.id];

      if (source) {
        counts[view.id] = source.outstanding();
      }
    }

    return counts;
  });

  resolvedCount = computed(() => countResolved(this.views()));
  totalCount = computed(() => this.views().length);

  // Shared with the exit guard, which asks the same question the panel's Done button does: is
  // there anything left in this run for the manager to lose by leaving? A step they hold no
  // permission for is not something they can lose, so it does not hold this false — the progress
  // count is where those steps still register as unsettled.
  nothingLeftToDo = computed(() => hasNothingLeftToDo(this.views()));
}
